import { CalendarMonth } from '@interhyp/platform-icons';
import Tippy from '@tippyjs/react/headless';
import classnames from 'classnames';
import { format, parse, isAfter, isBefore } from 'date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DatepickerCalender } from "./components/datepickerCalendar/DatepickerCalendar.js";
import { Locales } from "../../helper/Locales.js";
import { useIsMobile } from "../../helper/hooks/useIsMobile.js";
import { useMergedRef } from "../../helper/hooks/useMergedRef.js";
import { useResizeObserver } from "../../helper/hooks/useResizeObserver.js";
import { Input } from "../input/Input.js";
import styles from "./Datepicker.module.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';
export const Datepicker = ({
  value,
  className,
  onSelection,
  onBlur,
  onBlurFn,
  onIconClick,
  locale = 'de',
  isDateDisabledInDesktopPicker,
  inputRef,
  ...inputProps
}) => {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const [datepickerAlign, setDatepickerAlign] = useState('right');
  const containerRef = useRef(null);
  const isMobile = useIsMobile();
  const dateFormat = Locales[locale];
  const datepickerRef = useRef(null);
  const mergedInputRef = useMergedRef(inputRef ?? null, datepickerRef);
  useResizeObserver(containerRef, rect => {
    const {
      width
    } = rect;
    if (width < 290) {
      setDatepickerAlign('left');
    } else {
      setDatepickerAlign('right');
    }
  });
  function handleSelection(day, formattedDay) {
    setOpen(false);
    onSelection(day, formattedDay);
  }
  function handleIconClick() {
    if (!inputProps.disabled) {
      setOpen(true);
      setFocus(true);
      onIconClick?.();
    }
  }
  function handleFocus() {
    setOpen(false);
    setFocus(true);
  }
  const handleBlur = useCallback(e => {
    if (containerRef.current?.contains(e.relatedTarget)) {
      return;
    }

    // @deprecated onBlurFn property and should be removed within the next big release
    onBlurFn?.(e);
    onBlur?.(e);
    setFocus(false);
  }, [onBlur, onBlurFn]);
  const isDateDisabledIncludingMinMax = useCallback(date => {
    const getDate = input => typeof input === 'number' ? input : parse(input, DEFAULT_DATE_FORMAT, new Date());
    return inputProps.max && isAfter(date.getTime(), getDate(inputProps.max)) || inputProps.min && isBefore(date.getTime(), getDate(inputProps.min)) || isDateDisabledInDesktopPicker?.(date) || false;
  }, [inputProps.max, inputProps.min, isDateDisabledInDesktopPicker]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      ref: containerRef,
      className: classnames(styles.datepicker, className),
      children: [/*#__PURE__*/_jsx(Input, {
        icon: CalendarMonth,
        format: {
          format: dateFormat.date.format,
          mask: dateFormat.date.mask
        },
        ...inputProps,
        inputRef: mergedInputRef,
        onFocus: handleFocus,
        onIconClick: handleIconClick,
        onBlur: handleBlur,
        value: value,
        placeholder: focus || inputProps.fixedLabel ? dateFormat.date.displayFormat : undefined
      }), isMobile && /*#__PURE__*/_jsx(MobileDatepicker, {
        onSelection: handleSelection,
        dateFormat: dateFormat,
        inputProps: inputProps,
        value: value
      })]
    }), !isMobile && /*#__PURE__*/_jsx(DesktopDatepicker, {
      open: open,
      onSelection: handleSelection,
      locale: locale,
      value: value,
      parentRef: containerRef,
      align: datepickerAlign,
      isDateDisabled: isDateDisabledIncludingMinMax,
      initialMonth: inputProps.min ? new Date(new Date(inputProps.min).getFullYear(), new Date(inputProps.min).getMonth(), 1) : undefined,
      onClickOutside: (_, event) => {
        setOpen(false);
        setFocus(false);

        // force focus event, so we get the correct blur event
        datepickerRef?.current?.focus();
        datepickerRef?.current?.blur();
      }
    })]
  });
};
function MobileDatepicker({
  onSelection,
  value,
  inputProps,
  dateFormat
}) {
  function handleNativeInputChange(e) {
    const formattedDate = e.target.value;
    const date = parse(formattedDate, DEFAULT_DATE_FORMAT, new Date());
    onSelection(date, format(date, dateFormat.date.dateFormat));
  }
  let selectedDay = format(new Date(), DEFAULT_DATE_FORMAT);
  if (value) {
    const date = parse(value, dateFormat.date.dateFormat, new Date());
    if (!Number.isNaN(date.getTime())) {
      selectedDay = format(date, DEFAULT_DATE_FORMAT);
    }
  }
  return /*#__PURE__*/_jsx("input", {
    ...inputProps,
    value: selectedDay,
    onChange: handleNativeInputChange,
    className: styles.mobileInput,
    ref: inputProps.inputRef,
    type: "date"
  });
}
function DesktopDatepicker({
  open,
  onSelection,
  value,
  locale = 'de',
  align = 'right',
  children,
  parentRef,
  onClickOutside,
  isDateDisabled,
  initialMonth
}) {
  const [appendTo, setAppendTo] = useState();
  useEffect(() => {
    setAppendTo(document.querySelector('#root') ?? document.body);
  }, []);
  return /*#__PURE__*/_jsx(Tippy, {
    reference: parentRef,
    visible: open,
    interactive: true,
    onClickOutside: onClickOutside,
    placement: align === 'left' ? 'bottom-start' : 'bottom-end',
    offset: [0, 0],
    appendTo: appendTo,
    render: attr => /*#__PURE__*/_jsx("div", {
      role: "dialog",
      className: styles.datePickerContent,
      ...attr,
      children: /*#__PURE__*/_jsx(DatepickerCalender, {
        locale: locale,
        onSelection: onSelection,
        value: value,
        isDateDisabled: isDateDisabled,
        initialMonth: initialMonth
      })
    }),
    children: children
  });
}